import { Injectable, Injector, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';

/** The product name as it appears in every browser tab title. */
export const APP_BRAND = 'LumberLinq';

/** Used when a screen has no title of its own, so a tab never keeps the previous screen's name. */
export const APP_DEFAULT_TITLE = 'LumberLinq – Timber Management Software';

export const APP_DEFAULT_DESCRIPTION =
  'LumberLinq is timber management software for traders and exporters: digital tally sheets and measurement, ' +
  'consignments and shipments, stock units, inventory, packing lists, reports and reconciliation in one app.';

/**
 * Owns the browser tab title and the search/share meta tags (description, robots, og:*).
 *
 * Two sources, in this order:
 *  1. a screen's own title ({@link setScreenTitle}), for titles only the screen knows (an open document's name, a mode),
 *  2. the title written on the route (`title:` in the routing files), applied by AppTitleStrategy.
 *
 * Every title is shown as "<page> | LumberLinq". A title that already contains the brand is left as written.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  // Looked up lazily: the Router itself creates the title strategy that uses this service,
  // so asking for the Router in the constructor would be a circular dependency.
  private readonly injector = inject(Injector);

  private routeTitle: string | null = null;
  private screenTitle: string | null = null;
  private description: string | null = null;
  private indexable = false;
  private watching = false;
  private lastPath = '';
  private readonly defaultDescription: string;

  constructor() {
    this.defaultDescription = this.meta.getTag('name="description"')?.content || APP_DEFAULT_DESCRIPTION;
  }

  /** Called by AppTitleStrategy after every successful navigation. */
  applyRoute(title: string | null | undefined, description: string | null | undefined, indexable: boolean): void {
    this.watchNavigation();
    this.routeTitle = title ?? null;
    this.description = description ?? null;
    this.indexable = indexable;
    this.render();
  }

  /**
   * A screen that knows a better title than its route (for example the name of the document it opened) calls this.
   * It lasts until the user moves to a different page, then the next screen's own title takes over.
   * Pass null to go back to the route's title.
   */
  setScreenTitle(page: string | null): void {
    this.watchNavigation();
    this.screenTitle = page && page.trim() ? page.trim() : null;
    this.render();
  }

  /** "<page> | LumberLinq", or the default title when there is no page name. */
  compose(page: string | null | undefined): string {
    let base = (page ?? '').trim();
    // Older route titles were written "Page - LumberLinq": take the old suffix off so every title ends the same way.
    base = base.replace(/\s*[-–—|·]\s*Lumber\s?Linq\s*$/i, '').trim();
    if (!base || /^Lumber\s?Linq$/i.test(base)) {
      return APP_DEFAULT_TITLE;
    }
    return /Lumber\s?Linq/i.test(base) ? base : `${base} | ${APP_BRAND}`;
  }

  private render(): void {
    const full = this.compose(this.screenTitle ?? this.routeTitle);
    const description = this.description ?? this.defaultDescription;
    this.title.setTitle(full);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: this.indexable ? 'index, follow' : 'noindex, nofollow' });
    this.meta.updateTag({ property: 'og:title', content: full });
    this.meta.updateTag({ property: 'og:description', content: description });
  }

  /** A screen's own title belongs to that page only; forget it as soon as the path changes (not for ?query changes). */
  private watchNavigation(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;
    const router = this.injector.get(Router);
    this.lastPath = router.url.split(/[?#]/)[0];
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const path = event.url.split(/[?#]/)[0];
        if (path !== this.lastPath) {
          this.screenTitle = null;
        }
        this.lastPath = path;
      }
    });
  }
}
